import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { useRentersPolicy } from '@/shared/hooks/queries/use-renters-policy'
import { useCreateRentersClaim } from '@/shared/hooks/queries/use-create-renters-claim'
import { useIsOffline } from '@/shared/hooks/use-is-offline'
import { useUser } from '@/shared/hooks/use-user'
import { formatApiDate, formatDate } from '@/shared/helpers/format-date'
import { normalizePhone } from '@/shared/helpers/normalize-phone'
import { ROUTES } from '@/shared/constants/routes'
import { useClaimFormSteps } from '@/shared/hooks/use-claim-form-steps'

const FORM_STEP_COUNT = 2

const STEP_ONE_FIELDS = [
  'policyId',
  'dateOfLoss',
  'description',
  'phone',
] as const

export type RentersClaimFormValues = {
  policyId: string
  dateOfLoss: Date | undefined
  description: string
  phone: string
  documents: File[]
  photos: File[]
}

export function useClaimsRentersPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isOffline = useIsOffline()
  const { user } = useUser()
  const { currentStep, isFlipping, isLastStep, flipToStep, handleBack } =
    useClaimFormSteps(FORM_STEP_COUNT)
  const [isSuccessOpen, setIsSuccessOpen] = useState(false)
  const hasPrefilledPhoneRef = useRef(false)

  const { data: policies = [], isLoading: isPoliciesLoading } =
    useRentersPolicy()
  const createClaimMutation = useCreateRentersClaim()

  const formMethods = useForm<RentersClaimFormValues>({
    defaultValues: {
      policyId: '',
      dateOfLoss: undefined,
      description: '',
      phone: '',
      documents: [],
      photos: [],
    },
    mode: 'all',
  })

  const {
    watch,
    trigger,
    setValue,
    handleSubmit: formHandleSubmit,
  } = formMethods

  useEffect(() => {
    if (hasPrefilledPhoneRef.current || !user?.phone) return
    hasPrefilledPhoneRef.current = true
    setValue('phone', user.phone)
  }, [user?.phone, setValue])

  const selectedPolicyId = watch('policyId')
  const selectedPolicy = useMemo(
    () =>
      policies.find(
        (policy) => String(policy.policyExternalId) === selectedPolicyId,
      ),
    [policies, selectedPolicyId],
  )

  const submitClaim = formHandleSubmit((values) => {
    if (!values.dateOfLoss) return
    const phone = normalizePhone(values.phone)
    const hasPhoneChanged = !!user && normalizePhone(user.phone ?? '') !== phone
    createClaimMutation.mutate(
      {
        policyId: values.policyId,
        dateOfLoss: formatApiDate(values.dateOfLoss),
        description: values.description.trim(),
        phone,
        files: [...values.documents, ...values.photos],
        ...(hasPhoneChanged &&
          user && {
            profileUpdate: {
              givenName: user.givenName,
              surname: user.surname,
              phone,
            },
          }),
      },
      { onSuccess: () => setIsSuccessOpen(true) },
    )
  })

  const handleNext = useCallback(async () => {
    if (currentStep < FORM_STEP_COUNT - 1) {
      const isStepValid = await trigger([...STEP_ONE_FIELDS])
      if (isStepValid) flipToStep(currentStep + 1)
      return
    }
    submitClaim()
  }, [currentStep, trigger, flipToStep, submitClaim])

  function handleCancel() {
    navigate({ to: ROUTES.HOME })
  }

  function handleSuccessClose() {
    setIsSuccessOpen(false)
    navigate({ to: ROUTES.HOME })
  }

  return {
    formMethods,
    currentStep,
    isFlipping,
    isLastStep,
    hasSelectedPolicy: !!selectedPolicy,
    card: {
      planName: selectedPolicy?.dwellingType
        ? t(`onboarding.renters.dwellingTypes.${selectedPolicy.dwellingType}`)
        : '',
      ownerName: user?.fullName ?? '',
      policyId: `#${selectedPolicy?.policyExternalId ?? ''}`,
      startDate: formatDate(selectedPolicy?.startAt),
      address: selectedPolicy?.address?.address ?? '',
    },
    isDataLoading: isPoliciesLoading,
    isSubmitting: createClaimMutation.isPending,
    hasSubmitError: createClaimMutation.isError,
    isOffline,
    isSuccessOpen,
    handleNext,
    handleBack,
    handleCancel,
    handleSuccessClose,
  }
}
